/**
 * Bidirectional mapping between sidebar department names and URL paths.
 * Used to sync `selectedDepartment` state with the browser URL.
 */

const DEPARTMENT_TO_PATH: Record<string, string> = {
  'Dashboard':         '/dashboard',
  'Sales':             '/sales',
  'Operations':        '/operations',
  'Marketing':         '/marketing',
  'Finances':          '/finances',
  'EOS':               '/eos',
  'Engineering':       '/engineering',
  'Contacts':          '/contacts',
  'Clients':           '/clients',
  'Time Tracking':     '/time-tracking',
  'Comms':             '/comms',
  'Time Reports':      '/time-reports',
  'Settings':          '/settings',
  'My Week':           '/my-week',
  'My Scorecard':      '/my-scorecard',
  'My Tickets':        '/my-tickets',
  'My Time':           '/my-time',
  'Inbox':             '/inbox',
  'HelpDesk':          '/helpdesk',
  // rail v2: "Personal Inbox" → "Email". Canonical path is now /email; the old
  // /personal-inbox URL redirects (LEGACY_REDIRECTS). Internal department key
  // stays 'PersonalInbox' so the AppShell dispatch + InboxModule mode are unchanged.
  'PersonalInbox':     '/email',
  // rail v2: To-Dos pulled out of Operations to a top-level tool. Canonical
  // /todos renders the same graduated todos surface; /operations/todos redirects.
  'Todos':             '/todos',
  'Reports':           '/reports',
  // Analytics (Brain Dead To Read S1-c1, 2026-08-21): the one door to
  // Scorecard / Forecasting / Time Reports / Data Center / Finances — each a
  // path segment under it, owned by AnalyticsHubView's parser. The old homes
  // redirect (LEGACY_REDIRECTS, query strings carried).
  'Analytics':         '/analytics',
  // Call Intelligence (Data section). Was an orphan path: with no entry here,
  // departmentToPath('Call Intelligence') fell through to the `|| '/dashboard'`
  // fallback, so clicking the nav item bounced to the Dashboard, and a
  // cold-load of /call-intelligence resolved back to 'Dashboard'. Mapping it
  // both directions fixes the redirect.
  'Call Intelligence': '/call-intelligence',
  'Templates':         '/templates',
  'Scheduling':        '/scheduling',
  // Forms module (Tools section) — own surface beside Scheduling. List at
  // /forms; builder at /forms/:formId (relocated from /templates/forms/:id).
  // Public fill page lives at /f/:slug (App.tsx), NOT under /forms/*.
  'Forms':             '/forms',
  // Documentation Nation (Tools section) — client process documentation on
  // the flowchart substrate. Hub at /documentation; canvas at
  // /documentation/canvas/:projectId (sub-routes owned by the view).
  'Documentation Nation': '/documentation',
  // Proposals (Tools section, state.md Entry 137) — the Proposal Generator
  // promoted to a top-level tool. Library at /proposals; wizard at
  // /proposals/new; owner catalog at /proposals/catalog; detail at
  // /proposals/:id (sub-routes owned by ProposalsView). Old
  // /sales/proposals/* URLs redirect.
  // rail v2: "Proposals" → "Contracts". Canonical path is now /contracts (+ its
  // sub-routes); the old /proposals/* URLs redirect. Internal key stays
  // 'Proposals' so the dispatch + ProposalsView parser are unchanged.
  'Proposals':         '/contracts',
  'Mental Herd':       '/knowledge-base',
  // Smoke Tests (Admin) — the in-app QA checklist runner. Page at /smoke-tests;
  // ?run=<token> shows a finished run's export. Admin-gated at the nav + API.
  'SmokeTests':        '/smoke-tests',
  // AI Mission Control department (2026-06-22) — global AI usage + the agent
  // tiles. Overview at /mission-control; AI Agents tab at /mission-control/agents;
  // the Workflow Brain at /mission-control/agents/documentation-nation (sub-paths
  // owned by AiMissionControlView's parser). Owner/admin-gated at nav + API.
  'AiMissionControl':  '/mission-control',
};

const PATH_TO_DEPARTMENT: Record<string, string> = {};
for (const [dept, path] of Object.entries(DEPARTMENT_TO_PATH)) {
  PATH_TO_DEPARTMENT[path] = dept;
}

/** Canonical URLs for items rolled into Reports hub (no longer top-level path keys). */
const REPORTS_HUB_ALIASES: Record<string, string> = {
  Scorecards: '/reports/scorecards',
  Insights: '/reports/insights',
  Forecasting: '/reports/forecasting',
};

/** Return the URL path for a given department name. */
export function departmentToPath(department: string, subPath?: string): string {
  const reportsAlias = REPORTS_HUB_ALIASES[department];
  if (reportsAlias) {
    if (!subPath) return reportsAlias;
    return `${reportsAlias}/${subPath}`;
  }
  const base = DEPARTMENT_TO_PATH[department] || '/dashboard';
  if (!subPath) return base;
  return `${base}/${subPath}`;
}

/**
 * Parse a pathname into the top-level department + any remaining sub-path.
 *
 * Examples:
 *   '/'                        → { department: <user home page>, subPath: '' }
 *   '/operations'              → { department: 'Operations', subPath: '' }
 *   '/clients'                 → { department: 'Clients',    subPath: '' }
 *   '/clients/42'              → { department: 'Clients',    subPath: '42' }
 *   '/contacts/7'              → { department: 'Contacts',   subPath: '7' }
 *   '/sales/leads'             → { department: 'Sales',      subPath: 'leads' }
 *   '/sales/contacts'          → redirect to /contacts (lives under Data)
 */
const LEGACY_REDIRECTS: Record<string, { department: string; rewritePrefix: string }> = {
  // Phase 0 leads sunset (2026-08-20): the marketing 'processes' surface is
  // deleted; old links land on the Marketing hub. (The dead /sales sub-paths
  // — scraper, leads, events, insights, pipeline-settings — are NOT listed
  // here on purpose: SalesView's parser folds every unknown sub-path into a
  // redirect to /sales — the BSG Deals board since c4 — so one rule covers
  // them all without a prefix entry that could shadow live routes. The pre-c4
  // board + deal URLs (/sales/pipelines[/:pid[/cards/:id]], /sales/deals/:id)
  // are redirected by SalesView too, per ALIGNMENT §6.)
  '/marketing/processes': { department: 'Marketing', rewritePrefix: '/marketing' },
  '/operations/clients': { department: 'Clients', rewritePrefix: '/clients' },
  '/sales/contacts': { department: 'Contacts', rewritePrefix: '/contacts' },
  // Legacy /meetings sunset (2026-08-20): the old TipTap meeting-doc surface
  // is deleted; the graduated program at /eos/meetings is the one Meetings
  // page. Same playbook as /phones→/comms: old URLs keep resolving. The old
  // ?open=<doc id> deep link is NOT honored — those documents retired with
  // the surface — so a stale bookmark lands on the hub, which is honest.
  '/eos/l10': { department: 'EOS', rewritePrefix: '/eos/meetings' },
  '/meetings': { department: 'EOS', rewritePrefix: '/eos/meetings' },
  '/scorecards': { department: 'Analytics', rewritePrefix: '/analytics/scorecard' },
  // S4 (Brain Dead To Read): Insights folds into the Data Center — clean slate, no auto-convert (design review D3).
  '/insights': { department: 'Analytics', rewritePrefix: '/analytics/data-center' },
  '/reports/insights': { department: 'Analytics', rewritePrefix: '/analytics/data-center' },
  // Analytics hub (2026-08-21): the five surfaces moved under one door. Old
  // homes keep resolving, sub-paths carried (AppShell carries the query
  // string too — the digest emails deep-link /reports/scorecards?metric=).
  // /reports/insights is absorbed by the Data Center (S4) — see LEGACY_REDIRECTS.
  '/reports/scorecards': { department: 'Analytics', rewritePrefix: '/analytics/scorecard' },
  '/reports/capacity': { department: 'Analytics', rewritePrefix: '/analytics/forecasting' },
  '/time-reports': { department: 'Analytics', rewritePrefix: '/analytics/time-reports' },
  '/finances': { department: 'Analytics', rewritePrefix: '/analytics/finances' },
  // Old hours-based Forecasting deleted (Phase 0, 2026-08-20) — the capacity
  // board under Reports is the one forecasting surface; both old addresses
  // land there.
  '/forecasting': { department: 'Analytics', rewritePrefix: '/analytics/forecasting' },
  '/reports/forecasting': { department: 'Analytics', rewritePrefix: '/analytics/forecasting' },
  // T7 — Phones surface sunset. /phones routes redirect to /comms so any
  // bookmarks / email links / inter-module nav pointing at the old path
  // continue to work; selectedDepartment resolves to 'Comms'.
  '/phones': { department: 'Comms', rewritePrefix: '/comms' },
  // Sandbox sunset (2026-08-18, dead-code final): the /sandbox department is
  // retired — the prototype families were deleted (Claude Design is the
  // sandbox now) and the surviving sub-routes were always alternate mounts
  // of canonical surfaces. Same playbook as the /phones→/comms T7 sunset:
  // every old URL redirects to its canonical home, sub-paths carried, so
  // bookmarks and pasted links (e.g. a shared /engine/documentation-nation
  // canvas URL) keep resolving. Specific aliases FIRST — the bare /sandbox
  // catch-all must stay LAST of this group (insertion-order matching).
  '/operations/sandbox-board': { department: 'Sales', rewritePrefix: '/sales' },
  '/operations/sandbox-todos': { department: 'Todos', rewritePrefix: '/todos' },
  '/sandbox/operations-board': { department: 'Sales', rewritePrefix: '/sales' },
  '/sandbox/operations-todos': { department: 'Todos', rewritePrefix: '/todos' },
  '/sandbox/documentation-nation': { department: 'Documentation Nation', rewritePrefix: '/documentation' },
  '/sandbox/quickbooks-financials': { department: 'Finances', rewritePrefix: '/finances' },
  '/sandbox/fathom-hub': { department: 'Call Intelligence', rewritePrefix: '/call-intelligence' },
  '/sandbox/eos-l10': { department: 'EOS', rewritePrefix: '/eos' },
  '/sandbox/marketing-hub': { department: 'Marketing', rewritePrefix: '/marketing' },
  '/sandbox/form-builder': { department: 'Forms', rewritePrefix: '/forms' },
  '/sandbox/scheduling': { department: 'Scheduling', rewritePrefix: '/scheduling' },
  '/scheduling/playground': { department: 'Scheduling', rewritePrefix: '/scheduling' },
  '/sandbox': { department: 'Dashboard', rewritePrefix: '/dashboard' },
  // G1.1: legacy /sales/pipeline (singular, the pre-graduation kanban) bounces
  // to the board. It used to land on /sales/pipelines (the post-G1 tiles
  // index); BSG Deals c4 deleted that index and the board renders at bare
  // /sales, so the bounce goes straight there. Bookmarks + the FollowUps
  // "View" buttons still work.
  '/sales/pipeline': { department: 'Sales', rewritePrefix: '/sales' },
  // rail v2 renames — old URLs redirect to the new canonical paths so bookmarks,
  // email links and inter-module nav keep working. Backend /api/* routes are
  // unchanged; only the browser-facing paths move.
  '/personal-inbox': { department: 'PersonalInbox', rewritePrefix: '/email' },        // Personal Inbox → Email
  '/proposals': { department: 'Proposals', rewritePrefix: '/contracts' },             // Proposals → Contracts
  '/sales/proposals': { department: 'Proposals', rewritePrefix: '/contracts' },       // legacy double-prefix → Contracts
  '/sales/leads': { department: 'Sales', rewritePrefix: '/sales/scraper' },           // Lead Discovery → Scraper
  // rail v2 — To-Dos pulled out of Operations to a top-level tool.
  '/operations/todos': { department: 'Todos', rewritePrefix: '/todos' },
  // Phase 3 — retired Operations surfaces. The Processes browse-surface and the
  // standalone Action Plans surface are gone; old URLs bounce to the Operations
  // hub. (Backend + tables untouched; "action plan" is succeeded by the Sprint.)
  '/operations/processes': { department: 'Operations', rewritePrefix: '/operations' },
  '/operations/action-plans': { department: 'Operations', rewritePrefix: '/operations' },
  // Calendar surface (Velvet Grove v1) — short alias for the unified calendar.
  // Canonical mount is /scheduling/calendar (SchedulingView sub-path parser);
  // a top-level /calendar dispatch arm in AppShell is the deferred promotion.
  // NOTE: the AppShell redirect drops query params — share links should use
  // the canonical /scheduling/calendar?… form (what the address bar shows).
  '/calendar': { department: 'Scheduling', rewritePrefix: '/scheduling/calendar' },
};

// CD-12 / 4A: the orphaned department tile-hubs (/operations /marketing
// /reports) are SUNSET — nothing links to them, and they duplicate the rail's
// canonical sub-surfaces. The bare department URL redirects to its primary
// sub-surface. Matched EXACT-only (never as a prefix), so /operations/projects,
// /marketing/content, /reports/scorecards etc. are untouched. (Deleting the
// now-unreachable hub tile-grid JSX inside each department view is a
// follow-up cleanup — deferred-items.) /sales left this list with BSG Deals
// c4 (2026-09-18): the board renders AT /sales, so the bare URL is a real
// surface again, not a hub to bounce off.
const HUB_SUNSET: Record<string, { department: string; rewritePrefix: string }> = {
  '/operations': { department: 'Operations', rewritePrefix: '/operations/projects' },
  '/marketing': { department: 'Marketing', rewritePrefix: '/marketing/content' },
  '/reports': { department: 'Analytics', rewritePrefix: '/analytics/scorecard' },
};

export function parsePathname(pathname: string): { department: string; subPath: string; redirect?: string } {
  const clean = pathname.replace(/\/+$/, '') || '/';

  // Hub sunset (exact-match only — must run before the prefix-matching loop).
  if (HUB_SUNSET[clean]) {
    const { department, rewritePrefix } = HUB_SUNSET[clean];
    return { department, subPath: '', redirect: rewritePrefix };
  }

  for (const [legacy, { department, rewritePrefix }] of Object.entries(LEGACY_REDIRECTS)) {
    if (clean === legacy) {
      return { department, subPath: '', redirect: rewritePrefix };
    }
    if (clean.startsWith(legacy + '/')) {
      const sub = clean.slice(legacy.length + 1);
      return { department, subPath: sub, redirect: `${rewritePrefix}/${sub}` };
    }
  }

  if (clean === '/') {
    const homeKey = localStorage.getItem('bsg-home-page');
    if (homeKey) {
      const homePath = '/' + homeKey;
      if (PATH_TO_DEPARTMENT[homePath]) {
        return { department: PATH_TO_DEPARTMENT[homePath], subPath: '' };
      }
    }
    return { department: 'Dashboard', subPath: '' };
  }

  if (PATH_TO_DEPARTMENT[clean]) {
    return { department: PATH_TO_DEPARTMENT[clean], subPath: '' };
  }

  // Prefix match for sub-paths (longest match first by iterating all entries)
  let bestDept = '';
  let bestLen = 0;
  for (const [path, dept] of Object.entries(PATH_TO_DEPARTMENT)) {
    if (path !== '/' && clean.startsWith(path + '/') && path.length > bestLen) {
      bestDept = dept;
      bestLen = path.length;
    }
  }

  if (bestDept) {
    return { department: bestDept, subPath: clean.slice(bestLen + 1) };
  }

  return { department: 'Dashboard', subPath: '' };
}
